import React from 'react';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { lazyAddinsInitializer } from '../utils/lazyCore/lazyAddinsInitializer';
import { trace } from 'owa-trace';
import { isNetworkAvailable } from 'owa-offline';
import { logUsage } from 'owa-analytics';
import { useNetworkRetry } from './useNetworkRetry';

export interface UseAddinsProps {
    viewState: ComposeViewState;
    targetWindow: Window;
    isGetSetBodyAPIImprovementsEnabled: boolean;
    isComposeInitRefactorEnabled: boolean;
    initialized: React.MutableRefObject<boolean>;
    cleanupRef: React.MutableRefObject<(() => void) | null>;
    abortControllerRef: React.MutableRefObject<AbortController | null>;
}

/**
 * Custom React hook for managing mail add-ins initialization and cleanup lifecycle.
 *
 * This hook handles the initialization of mail add-ins with proper race condition prevention,
 * abort handling, cleanup management, and network-based retry mechanism. It uses feature flags
 * to control when the refactored implementation should be used.
 *
 * @param props - Configuration object for the add-ins hook
 * @param props.viewState - The current view state containing compose ID and mailbox information
 * @param props.targetWindow - The target window where add-ins will be rendered
 * @param props.isGetSetBodyAPIImprovementsEnabled - Feature flag for body API improvements
 * @param props.isComposeInitRefactorEnabled - Feature flag to enable the refactored initialization
 * @param props.initialized - Ref to track initialization state to prevent duplicate initialization
 * @param props.cleanupRef - Ref to store cleanup function for proper resource management
 * @param props.abortControllerRef - Ref to store abort controller for canceling ongoing operations
 *
 * @remarks
 * - Only runs when `isComposeInitRefactorEnabled` feature flag is true
 * - Prevents race conditions by aborting previous initialization attempts
 * - Lazily loads the add-ins initializer to improve performance
 * - Properly handles cleanup on component unmount or dependency changes
 * - Includes error handling with logging for failed initializations
 * - Implements network-based retry mechanism for offline scenarios
 * - Tracks offline launch attempts and successful recovery for telemetry
 *
 * @example
 * ```typescript
 * const cleanupRef = useRef<(() => void) | null>(null);
 * const abortControllerRef = useRef<AbortController | null>(null);
 * const initialized = useRef(false);
 *
 * useAddins({
 *   viewState,
 *   targetWindow: window,
 *   isGetSetBodyAPIImprovementsEnabled: true,
 *   isComposeInitRefactorEnabled: true,
 *   initialized,
 *   cleanupRef,
 *   abortControllerRef,
 * });
 * ```
 */
export function useAddins({
    viewState,
    targetWindow,
    isGetSetBodyAPIImprovementsEnabled,
    isComposeInitRefactorEnabled,
    initialized,
    cleanupRef,
    abortControllerRef,
}: UseAddinsProps): void {
    const composeId = viewState.composeId;

    // Clear and simple initialization function
    const initializeAddins = React.useCallback(async (): Promise<(() => void) | null> => {
        // Abort any existing initialization
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        trace.info(
            `ExtAddins: ComposeAddins: Starting initialization\n` +
                `isOnline: ${isNetworkAvailable()},\n` +
                `composeId: ${composeId},\n` +
                `initialized: ${initialized.current}`
        );

        try {
            const initializeAddinsFunction = await lazyAddinsInitializer.import();

            const cleanupFn = await initializeAddinsFunction(
                viewState,
                targetWindow,
                isGetSetBodyAPIImprovementsEnabled
            );

            return cleanupFn;
        } catch (error) {
            if (abortControllerRef.current?.signal.aborted) {
                return null;
            }

            const errorMessage = error instanceof Error ? error.message : String(error);

            // Log different events based on network status
            if (!isNetworkAvailable()) {
                /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param, owa-custom-rules/forbid-specific-functions-on-catch-finally-blocks -- (https://aka.ms/OWALintWiki)
                 * BASELINE: DO NOT COPY/PASTE!
                 * undefined
                 *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo
                 (https://aka.ms/OWALintWiki)
                                 * Baseline. Dev touching this file must update to pass the active MailboxInfo; Docs: https://aka.ms/owa-analytics-mailboxinfo
                                 * undefined
                                 *	> MailboxInfo must be passed into the function to identify the account.
                                 (https://aka.ms/OWALintWiki)
                                                 * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error, use PerformanceDatapoint instead.
                                                 *	> Do not call 'logUsage' from 'owa-analytics' inside catch or finally blocks */
                logUsage('ComposeAddins_OfflineLaunch', undefined, {
                    composeId,
                    errorMessage,
                });
            } else {
                /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param, owa-custom-rules/forbid-specific-functions-on-catch-finally-blocks -- (https://aka.ms/OWALintWiki)
                 * BASELINE: DO NOT COPY/PASTE!
                 * undefined
                 *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo
                 (https://aka.ms/OWALintWiki)
                                 * Baseline. Dev touching this file must update to pass the active MailboxInfo; Docs: https://aka.ms/owa-analytics-mailboxinfo
                                 * undefined
                                 *	> MailboxInfo must be passed into the function to identify the account.
                                 (https://aka.ms/OWALintWiki)
                                                 * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error, use PerformanceDatapoint instead.
                                                 *	> Do not call 'logUsage' from 'owa-analytics' inside catch or finally blocks */
                logUsage('ComposeAddins_InitializationFailure', undefined, {
                    composeId,
                    errorMessage,
                });
            }

            throw error;
        }
    }, [
        viewState,
        targetWindow,
        isGetSetBodyAPIImprovementsEnabled,
        composeId,
        abortControllerRef,
    ]);

    // Handle retry signal from network hook
    const handleRetrySignal = React.useCallback(() => {
        // Only retry if not already initialized
        if (initialized.current) {
            return;
        }

        trace.info(`ExtAddins: ComposeAddins: Retry signal received\n` + `composeId: ${composeId}`);

        // Add small delay to allow network to stabilize
        const timeoutId = targetWindow.setTimeout(() => {
            initializeAddins()
                .then(cleanupFn => {
                    // Check if this initialization was aborted
                    if (abortControllerRef.current?.signal.aborted) {
                        return;
                    }

                    if (cleanupFn) {
                        cleanupRef.current = cleanupFn;
                        initialized.current = true;

                        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
                         * BASELINE: DO NOT COPY/PASTE!
                         *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
                        logUsage('ComposeAddins_OfflineRecovery', undefined, {
                            composeId,
                        });

                        trace.info(
                            `ExtAddins: ComposeAddins: Network recovery successful\n` +
                                `composeId: ${composeId}`
                        );
                    }
                })
                .catch(error => {
                    // Only log if not aborted
                    if (!abortControllerRef.current?.signal.aborted) {
                        trace.warn('ExtAddin - compose addins network recovery failed: ', error);
                    }
                });
        }, 100);

        return () => {
            targetWindow.clearTimeout(timeoutId);
        };
    }, [composeId, initializeAddins, initialized, cleanupRef, targetWindow, abortControllerRef]);

    // Get network state from the network retry hook
    useNetworkRetry({
        enabled: isComposeInitRefactorEnabled,
        composeId,
        isInitialized: () => initialized.current,
        onRetrySignal: handleRetrySignal,
    });

    /**
     * Primary add-in initialization effect that handles initial setup and cleanup.
     * Manages initialization based on feature flags and network availability.
     * Sets up cleanup to properly handle abort controllers and cleanup functions.
     */
    React.useEffect(() => {
        // Only run the refactored implementation if the feature flag is enabled
        if (!isComposeInitRefactorEnabled) {
            return () => {};
        }

        // Skip if already initialized for this compose ID
        if (initialized.current) {
            return () => {};
        }

        // Try to initialize addins immediately if online
        if (isNetworkAvailable()) {
            initializeAddins()
                .then(cleanupFn => {
                    // Check if this initialization was aborted
                    if (abortControllerRef.current?.signal.aborted) {
                        return;
                    }

                    // Store cleanup function for later use (only if it exists)
                    if (cleanupFn) {
                        cleanupRef.current = cleanupFn;
                        initialized.current = true;
                    }
                })
                .catch(error => {
                    // Only log if not aborted
                    if (!abortControllerRef.current?.signal.aborted) {
                        trace.warn('ExtAddin - compose addins initialization failed: ', error);
                    }
                });
        }

        // Return cleanup function that handles both abort and cleanup
        return () => {
            // Abort any ongoing initialization
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Call stored cleanup function if it exists
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }

            // Reset initialization state
            initialized.current = false;
        };
    }, [
        composeId,
        viewState.mailboxInfo,
        targetWindow,
        isGetSetBodyAPIImprovementsEnabled,
        initializeAddins,
    ]);
}
